import {
  Button,
  ButtonIcon,
  Caption,
  css,
  hex2rgba,
  InfoIcon,
  Inline,
  LineIcon,
  Plus1Icon,
  styled,
  Tooltip,
} from 'canoe';
import React, { ReactNode, useMemo } from 'react';

import { setSVGColor } from 'canoe/components/ButtonIcon/ButtonIcon';

export const SectionContent = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spaces.s};
  `}
`;

const Header = styled.div<{ isExpanded: boolean }>`
  ${({ theme, isExpanded }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spaces.xs};
    padding: ${theme.spaces.s} ${theme.spaces.s} ${isExpanded ? '0px' : theme.spaces.s};
    border-top: 1px solid ${theme.colors.GlitterGray};

    + ${SectionContent} {
      padding-top: ${theme.spaces.xs};
    }
  `}
`;

const StyledButtonIcon = styled(ButtonIcon)<{ isExpanded: boolean; notAllowed: boolean }>`
  ${({ theme, isExpanded, notAllowed }) => css`
    ${setSVGColor(theme.colors.OnyxGrayL30)};

    ${notAllowed &&
    css`
      cursor: not-allowed;
    `}
    &:focus {
      background-color: initial;
      box-shadow: initial;
      ${setSVGColor(theme.colors.OnyxGrayL30)}
    }

    &:hover {
      ${setSVGColor(theme.colors.OnyxGrayL30)}
    }

    ${isExpanded &&
    css`
      &,
      &:focus,
      &:hover {
        ${setSVGColor(theme.colors.OnyxGrayD20)}
      }
    `}
    &:hover {
      background-color: ${hex2rgba(theme.colors.GlitterGrayD10, 0.2)};
      box-shadow: ${theme.boxShadow.outline(hex2rgba(theme.colors.GlitterGrayD10, 0.2))};
    }
  `}
`;

const HeaderLabel = styled(Caption)<{
  withSuffix: boolean;
  isClickable: boolean;
  onClick?: VoidFunction;
}>`
  ${({ theme, withSuffix, isClickable }) => css`
    flex-grow: 1;
    font-weight: ${theme.fontWeights.bold};

    ${isClickable &&
    css`
      cursor: pointer;
    `}

    ${withSuffix &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
    `}
  `}
`;

interface Props {
  name: string;
  nameSuffix?: string;
  children: ReactNode;
  id?: string;
  onClick?: VoidFunction;
  isExpanded?: boolean;
  onToggle?: VoidFunction;
  toggleDisabled?: boolean;
  tooltip?: string;
}

const Section = ({
  name,
  nameSuffix,
  children,
  onClick,
  isExpanded,
  onToggle,
  toggleDisabled,
  tooltip,
  ...otherProps
}: Props) => {
  const isToggleDisabled = !!onToggle && !!isExpanded && !!toggleDisabled;
  const toggleTooltipContent = useMemo(() => {
    if (!onToggle) {
      return '';
    }
    if (isToggleDisabled) {
      return 'This is the default value from Styleguide and cannot be removed';
    }
    if (isExpanded) {
      return 'Remove style';
    }
    return 'Add style';
  }, [onToggle, isToggleDisabled, isExpanded]);

  return (
    <div {...otherProps}>
      <Header data-cy="toolbox-section-header" isExpanded={!onToggle || !!isExpanded}>
        <HeaderLabel
          isClickable={!!onClick || (!toggleDisabled && !!onToggle)}
          element="label"
          withSuffix={!!nameSuffix}
          color={!onToggle || isExpanded ? 'OnyxGrayD20' : 'OnyxGrayL30'}
          m="none"
          // if header label doesn't have a custom click event, then it should toggle the section
          onClick={!onClick && !toggleDisabled && !!onToggle ? onToggle : undefined}
        >
          {onClick ? (
            <Button
              appearance="text"
              variant="contrast"
              onClick={onClick}
              size="xsmall"
              inline
              showUnderlined
            >
              <span css={{ color: 'initial' }}>
                <Inline align="center" space="xxs">
                  {name}
                  {tooltip && (
                    <Tooltip content={tooltip}>
                      <InfoIcon size="s" />
                    </Tooltip>
                  )}
                </Inline>
              </span>
            </Button>
          ) : (
            <span>
              <Inline align="center" space="xxs">
                {name}
                {tooltip && (
                  <Tooltip content={tooltip}>
                    <InfoIcon size="s" />
                  </Tooltip>
                )}
              </Inline>
            </span>
          )}
          {!!nameSuffix && <span>{nameSuffix}</span>}
        </HeaderLabel>
        {onToggle && (
          <Tooltip
            content={
              <Caption mb="none" color="White" maxWidth={220}>
                {toggleTooltipContent}
              </Caption>
            }
            offset="xxs"
          >
            <StyledButtonIcon
              icon={isExpanded ? LineIcon : Plus1Icon}
              color="neutral"
              radius="small"
              onClick={isToggleDisabled ? undefined : onToggle}
              isExpanded={isExpanded}
              notAllowed={isToggleDisabled}
            />
          </Tooltip>
        )}
      </Header>
      {(!onToggle || !!isExpanded) && children && <SectionContent>{children}</SectionContent>}
    </div>
  );
};

export default Section;
